import React from 'react';
import { Helmet } from 'react-helmet-async';

// Serialize a JS object as JSON-LD safely — escapes `<` so a value like
// `</script>` in user-provided data can never break out of the <script> tag.
// (Belt-and-suspenders; today all inputs are LLM/backend-controlled, but the
// safe helper future-proofs against admin-editable schema values.)
const safeStringify = (obj) => JSON.stringify(obj).replace(/</g, '\\u003c');

/**
 * SEO component — title/meta/OG/Twitter/canonical/JSON-LD/breadcrumbs/hreflang.
 *
 * Usage:
 *   <SEO title="..." description="..." path="/blog/my-slug" />
 *   <SEO ... breadcrumbs={[{name:'Home',path:'/'},{name:'FAQ',path:'/faq'}]} />
 *   <SEO ... jsonLd={{...}} />   // additional schema (Article/Service/etc.)
 */
const SITE_NAME = 'ViratAstro - Vivek Mudgal Astrologer';
const SITE_URL = 'https://viratastro.cloud';
// Feb 08, 2026 — Master brand OG image system (photo + luxury 3D hybrid).
// New Authority-Hybrid images live at /brand/og/va-og-*.png (1200×630, PNG).
// The home variant is the site-wide fallback — it carries face-of-authority.
const DEFAULT_IMAGE = '/brand/og/va-og-home.png';

const buildBreadcrumbList = (breadcrumbs) => {
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((b, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: b.name,
      item: `${SITE_URL}${b.path || '/'}`,
    })),
  };
};

export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  keywords,
  jsonLd,
  breadcrumbs,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${SITE_URL}${path}`;
  const fullImage = image && image.startsWith('http') ? image : `${SITE_URL}${image || ''}`;
  const breadcrumbLd = buildBreadcrumbList(breadcrumbs);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Feb 2026 N1/N2/N24 SEO Fix — self-canonical per route.
          Every page emits ONE canonical based on its own `path` prop
          (query strings stripped in caller). Helmet REPLACES any
          static canonical from public/index.html because the base
          canonical there was removed on Feb 12 to eliminate duplicates. */}
      <link rel="canonical" href={url} />

      {/* Hreflang — Hindi default. English alternate points to SAME URL
          (no ?lang=en variant) — the app renders bilingual content in-place
          via LanguageContext, so ?lang=en was creating phantom duplicates
          in GSC (N19). */}
      <link rel="alternate" hrefLang="hi-IN" href={url} />
      <link rel="alternate" hrefLang="en-IN" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Robots — always emitted so Helmet REPLACES the static baseline
          `index, follow` meta in public/index.html. Prevents 404 pages
          from being seen as `index, follow` (N3). */}
      <meta
        name="robots"
        content={noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="hi_IN" />
      <meta property="og:locale:alternate" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* BreadcrumbList JSON-LD */}
      {breadcrumbLd && (
        <script type="application/ld+json">
          {safeStringify(breadcrumbLd)}
        </script>
      )}

      {/* Page-specific JSON-LD (Article/FAQPage/Service/etc.) — supports single object or array */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((schema, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {safeStringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
